import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { ArrowRight, Plus, Check, Store, CalendarDays, Sparkles, Megaphone } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuantityStepper from "../components/QuantityStepper";
import CheckoutView from "../components/CheckoutView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = "makers-pitch";

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n) || 0);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const PERKS = [
  { i: Store, t: "Your own pitch at FERAL Fest to showcase and sell your craft." },
  { i: Sparkles, t: "Includes the option to join one session a day at the festival if you'd like to." },
  { i: Megaphone, t: "Your fee goes straight into marketing and growing the festival — making it better for makers, guests and everyone." },
];

const PitchCard = ({ item, qty, onChange, featured }) => {
  const soldOut = item.soldOut;
  return (
    <div
      data-testid={`makers-pitch-${item.id}`}
      className={`relative flex flex-col rounded-lg p-7 md:p-8 transition-all duration-300 ${
        featured
          ? "bg-[#2c6052] text-[#f3ece1] shadow-2xl md:-translate-y-2 ring-2 ring-[#ebc754]"
          : "bg-white border border-[#d9c3a8] shadow-sm"
      } ${soldOut ? "opacity-60" : ""}`}
    >
      {featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ebc754] text-[#14110f] text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-1">
          Best value
        </span>
      )}
      <div className="flex items-center gap-2 mb-2">
        <CalendarDays size={18} className={featured ? "text-[#ebc754]" : "text-[#9b3d0a]"} />
        <h3 className={`feral-serif text-2xl ${featured ? "text-[#f3ece1]" : "text-[#9b3d0a]"}`}>{item.name}</h3>
      </div>
      <p className={`text-sm mb-5 ${featured ? "text-[#e2c7b0]" : "text-[#6e5646]"}`}>{item.desc}</p>
      <div className="flex items-end gap-2 mb-6">
        <span className={`feral-serif text-4xl leading-none ${featured ? "text-[#ebc754]" : "text-[#9b3d0a]"}`}>{gbp(item.price)}</span>
        <span className={`text-sm mb-1 ${featured ? "text-[#e2c7b0]" : "text-[#9a876f]"}`}>per pitch</span>
      </div>
      <div className="mt-auto pt-5 border-t border-[#e0d3bf]/40 flex items-center justify-between gap-3">
        {soldOut ? (
          <span className="bg-[#d9c3a8] text-[#7a6a58] text-xs font-bold tracking-[0.12em] uppercase px-5 py-3">Sold out</span>
        ) : qty > 0 ? (
          <QuantityStepper value={qty} onChange={onChange} testId={`makers-qty-${item.id}`} />
        ) : (
          <button
            data-testid={`makers-add-${item.id}`}
            onClick={() => onChange(1)}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 transition-colors ${
              featured ? "bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f]" : "bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de]"
            }`}
          >
            Add pitch <Plus size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default function MakersPitch() {
  const [event, setEvent] = useState(null);
  const [cart, setCart] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    document.title = "Makers' Pitch | FERAL Fest";
    axios.get(`${API}/events/${SLUG}`).then((r) => setEvent(r.data)).catch(() => {});
  }, []);

  const pitches = event?.dayTickets || [];

  const setQty = (id, qty) =>
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });

  const lines = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, qty]) => {
          const it = pitches.find((i) => i.id === id);
          if (!it) return null;
          return { id, qty, name: it.name, price: it.price, unit: it.unit };
        })
        .filter(Boolean),
    [cart, event] // eslint-disable-line
  );

  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const count = lines.reduce((s, l) => s + l.qty, 0);

  if (showCheckout) {
    return (
      <CheckoutView
        event={SLUG}
        items={Object.entries(cart).map(([id, qty]) => ({ id, qty }))}
        eventName="FERAL Fest — Makers' Pitch"
        lines={lines}
        total={subtotal}
        allowPromo={false}
        onBack={() => setShowCheckout(false)}
      />
    );
  }

  const oneDay = pitches.find((p) => p.id === "makers-1day");
  const weekend = pitches.find((p) => p.id === "makers-weekend");

  return (
    <div className="bg-[#efe6d7] min-h-screen flex flex-col" data-testid="makers-pitch-page">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#2c6052] text-[#f3ece1] pt-32 pb-20 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-[#ebc754] tracking-[0.28em] text-xs font-semibold uppercase mb-5">FERAL Fest · Makers</p>
          <h1 className="feral-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">Bring your craft to FERAL Fest</h1>
          <p className="text-[#e2c7b0] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-9">
            Grab a maker's pitch and join us at FERAL Fest. Sell your work, meet the community and be part of something wild.
            Every pitch fee goes straight back into growing the festival.
          </p>
          <button
            onClick={() => scrollTo("book")}
            data-testid="makers-hero-cta"
            className="inline-flex items-center gap-3 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-9 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors"
          >
            Book your pitch <ArrowRight size={17} />
          </button>
        </div>
      </section>

      {/* Perks */}
      <section className="px-6 py-16 max-w-4xl mx-auto w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PERKS.map((p, i) => (
            <div key={i} className="bg-white border border-[#d9c3a8] rounded-lg p-6 shadow-sm">
              <div className="w-11 h-11 rounded-full bg-[#f3e7d6] flex items-center justify-center mb-4">
                <p.i size={20} className="text-[#9b3d0a]" />
              </div>
              <p className="text-[#3a3a30] text-sm leading-relaxed">{p.t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Booking */}
      <section id="book" className="px-6 pb-24 max-w-4xl mx-auto w-full scroll-mt-24">
        <h2 className="feral-serif text-[#9b3d0a] text-3xl sm:text-4xl mb-2 text-center">Choose your pitch</h2>
        <p className="text-[#6e5646] text-center mb-10">Secure your spot in seconds — you'll get a confirmation email straight away.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {oneDay && <PitchCard item={oneDay} qty={cart[oneDay.id] || 0} onChange={(q) => setQty(oneDay.id, q)} />}
          {weekend && <PitchCard item={weekend} qty={cart[weekend.id] || 0} onChange={(q) => setQty(weekend.id, q)} featured />}
        </div>

        {count > 0 && (
          <div className="bg-white border border-[#d9c3a8] rounded-lg p-6 shadow-sm sticky bottom-6" data-testid="makers-summary">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs font-bold tracking-[0.12em] text-[#7a6a58] uppercase mb-1">
                  {count} pitch{count > 1 ? "es" : ""}
                </p>
                <p className="feral-serif text-[#9b3d0a] text-3xl leading-none">{gbp(subtotal)}</p>
              </div>
              <button
                onClick={() => setShowCheckout(true)}
                data-testid="makers-checkout-btn"
                className="bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] px-8 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors inline-flex items-center gap-2"
              >
                Book &amp; pay <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
